import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutData, siteInfo } from '@/mocks/anime';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9sojqh4ne17ei0sdl3g';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const faqData = [
    { q: '天天动漫是免费的吗？', a: '是的，天天动漫所有番剧资源均免费在线观看，无需付费会员即可畅享全部内容。' },
    { q: '天天动漫的番剧更新速度快吗？', a: '我们保持与日本和国内播出的同步更新，热门新番通常在播出后数小时内即可在天天动漫上观看。' },
    { q: '天天动漫支持手机观看吗？', a: '当然！天天动漫采用响应式设计，支持手机、平板和电脑等多种设备，随时随地追番看动漫。' },
    { q: '我可以推荐番剧给天天动漫吗？', a: '非常欢迎！您可以通过联系我们页面提交番剧推荐，我们的团队会认真评估每一条建议。' },
  ];

  return (
    <>
      <SEO
        title="关于我们"
        description={`了解${siteInfo.name}的品牌故事、平台口碑与联系方式。${siteInfo.tagline}，汇聚海量动漫资源，服务超过500万动漫爱好者。`}
        keywords="关于天天动漫,天天动漫品牌,天天动漫联系方式,动漫平台,联系我们"
        canonicalPath="/about"
      />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `天天动漫 - 关于我们`,
          description: `了解天天动漫的品牌故事、平台口碑与联系方式。`,
        }}
      />
      <SchemaOrg
        type="FAQPage"
        data={{
          mainEntity: faqData.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          })),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <Breadcrumb items={[{ name: '首页', href: '/' }, { name: '关于我们' }]} />

              <div className="mt-8 text-center">
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
                  {aboutData.brandIntro.title}
                </h1>
                <p className="text-lg text-foreground-600 mt-3">{aboutData.brandIntro.subtitle}</p>
              </div>

              <div className="mt-10">
                <div className="aspect-video rounded-xl overflow-hidden bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20anime%20studio%20office%20interior%20with%20warm%20lighting%2C%20shelves%20filled%20with%20manga%20and%20figures%2C%20cozy%20creative%20workspace%20atmosphere%2C%20soft%20ambient%20golden%20light%2C%20clean%20minimal%20aesthetic%2C%20anime%20art%20style&width=1600&height=900&seq=about-banner-01&orientation=landscape"
                    alt="天天动漫办公环境"
                    title="天天动漫 - 办公室环境"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="mt-10 prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed">
                <p className="text-base leading-8">{aboutData.brandIntro.description}</p>
                <p className="text-base leading-8 mt-4"><strong>我们的使命：</strong>{aboutData.brandIntro.mission}</p>
                <p className="text-base leading-8 mt-4"><strong>我们的愿景：</strong>{aboutData.brandIntro.vision}</p>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {aboutData.reputation.title}
                </h2>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {aboutData.reputation.items.map((item, index) => (
                  <div key={index} className="bg-background-100 rounded-xl p-6 text-center border border-background-200/70">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 md:w-14 md:h-14">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <p className="text-2xl font-heading font-bold text-foreground-950">{item.value}</p>
                    <p className="text-sm text-foreground-600 mt-1">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {aboutData.contact.title}
                </h2>
                <p className="text-sm text-foreground-600 mt-2">{aboutData.contact.description}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">电子邮箱</p>
                      <p className="text-sm text-foreground-600 mt-0.5">{aboutData.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">客服电话</p>
                      <p className="text-sm text-foreground-600 mt-0.5">{aboutData.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">公司地址</p>
                      <p className="text-sm text-foreground-600 mt-0.5">{aboutData.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">工作时间</p>
                      <p className="text-sm text-foreground-600 mt-0.5">{aboutData.contact.workingHours}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">发送消息</h3>
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="space-y-4"
                  >
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">留言内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您想说的话（最多500字）"
                      />
                    </div>


                    {formStatus === 'success' && (
                      <div className="p-3 bg-accent-100 text-accent-700 text-sm rounded-lg">
                        消息已发送成功！我们会尽快回复您。
                      </div>
                    )}
                    {formStatus === 'error' && (
                      <div className="p-3 bg-primary-100 text-primary-700 text-sm rounded-lg">
                        {formError}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer disabled:opacity-60"
                    >
                      {formStatus === 'loading' ? '发送中...' : '发送消息'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  常见问题
                </h2>
              </div>
              <div className="space-y-4">
                {faqData.map((item, index) => (
                  <details key={index} className="bg-background-100 rounded-xl border border-background-200/70 group">
                    <summary className="p-5 cursor-pointer text-sm md:text-base font-medium text-foreground-950 list-none flex items-center justify-between">
                      {item.q}
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-2">
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                      </span>
                    </summary>
                    <div className="px-5 pb-5 -mt-2">
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}